import React, { useEffect, useState } from 'react';
import { chatApi } from '../services/chatApi';
import { fmtDay, fmtDayTime, fmtSentRead, parseChatTime } from '../lib';
import { Dash } from './PlatformBits';

/** The cells every send table shares — a campaign's leads, a template's
 *  sends, the cadence sends: the lead's reply (fetched lazily from the
 *  chat, bounded concurrency) and the site visit it led to. */

interface Reply { text: string; at: Date; }
export type Replies = Record<string, Reply[] | 'error'>;

const REPLY_FETCH_CONCURRENCY = 4;

interface RepliedSend { id: string; replied_at: string | null; conversation_id: string | null; sent_at: string | null; }

/** For every replied send, the lead's messages after that send, once. */
export function useReplies(sends: RepliedSend[] | null): Replies {
  const [replies, setReplies] = useState<Replies>({});
  useEffect(() => {
    if (!sends) return;
    const targets = sends.filter((s) => s.replied_at && s.conversation_id && s.sent_at && replies[s.id] === undefined);
    if (!targets.length) return;
    let cancelled = false;

    const fetchOne = async (send: RepliedSend) => {
      try {
        const { messages } = await chatApi.getMessages(send.conversation_id!, { limit: 50 });
        const sentAt = new Date(send.sent_at!);
        const leadReplies = messages
          .filter((m) => m.sender_type === 1 && m.text && parseChatTime(m.date_time) >= sentAt)
          .map((m) => ({ text: m.text!, at: parseChatTime(m.date_time) }))
          .sort((a, b) => a.at.getTime() - b.at.getTime());
        if (!cancelled) setReplies((prev) => ({ ...prev, [send.id]: leadReplies }));
      } catch {
        if (!cancelled) setReplies((prev) => ({ ...prev, [send.id]: 'error' }));
      }
    };

    (async () => {
      for (let i = 0; i < targets.length; i += REPLY_FETCH_CONCURRENCY) {
        if (cancelled) return;
        await Promise.all(targets.slice(i, i + REPLY_FETCH_CONCURRENCY).map(fetchOne));
      }
    })();

    return () => { cancelled = true; };
  }, [sends, replies]);
  return replies;
}

/** The lead's words, as a small inbound bubble (content, not a card) —
 *  two lines, the rest a click away. */
export const ReplyCell: React.FC<{ repliedAt: string | null; rows: Reply[] | 'error' | undefined }> = ({ repliedAt, rows }) => {
  const [open, setOpen] = useState(false);
  return (
    <td className="px-3 py-3 min-w-[200px] max-w-[300px]">
      {!repliedAt && <Dash />}
      {repliedAt && rows === undefined && (
        <span className="inline-flex items-center gap-1.5 text-graphite text-xs">
          <span className="w-3 h-3 rounded-full border-[1.5px] border-sage border-t-forest animate-spin" />loading reply
        </span>
      )}
      {repliedAt && rows === 'error' && (
        <span className="text-xs"><span className="text-brick">couldn't load the reply</span>
          <span className="block text-[11px] text-graphite num">replied {fmtDayTime(repliedAt)}</span></span>
      )}
      {repliedAt && Array.isArray(rows) && (
        rows.length === 0
          ? <span className="text-xs text-amber-ink font-semibold">replied <span className="font-normal text-graphite num">{fmtDayTime(repliedAt)}</span></span>
          : (
            <div className="text-[13px] leading-snug">
              {(open ? rows : rows.slice(0, 1)).map((m, i) => (
                <div key={i} className={i ? 'mt-1.5' : ''}>
                  <div className={`inline-block max-w-full rounded-xl rounded-tl-sm bg-cream px-2.5 py-1.5 break-words whitespace-pre-wrap ${open ? '' : 'line-clamp-2'}`}
                       title={open ? undefined : m.text}>{m.text}</div>
                  {(open || i === 0) && <div className="text-[11px] text-graphite mt-0.5 num">{fmtDayTime(m.at.toISOString())}</div>}
                </div>
              ))}
              {rows.length > 1 && (
                <button onClick={() => setOpen(!open)} className="text-[11px] font-semibold text-forest hover:underline underline-offset-2 mt-0.5">
                  {open ? 'show less' : `+${rows.length - 1} more message${rows.length === 2 ? '' : 's'}`}
                </button>
              )}
            </div>
          )
      )}
    </td>
  );
};

/** The visit a send led to: conducted (solid, the headline), scheduled
 *  (ring), cancelled (struck). */
export const VisitCell: React.FC<{ visitedAt: string | null; status: 'open' | 'done' | 'cancelled' | null }> = ({ visitedAt, status }) => (
  <td className="px-3 py-3 whitespace-nowrap">
    {visitedAt ? (
      status === 'done' ? (
        <span className="inline-flex items-center gap-1.5 text-forest font-semibold text-[13px]">
          <span className="w-2 h-2 rounded-full bg-forest" />Conducted
          <span className="font-normal text-graphite text-[12px] num">{fmtDay(visitedAt)}</span>
        </span>
      ) : status === 'cancelled' ? (
        <span className="inline-flex items-center gap-1.5 text-graphite text-[13px]">
          <span className="w-2 h-2 rounded-full bg-charcoal/20" />Cancelled
          <span className="text-[12px] line-through num">{fmtDay(visitedAt)}</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-forest font-semibold text-[13px]">
          <span className="w-2 h-2 rounded-full border-[1.5px] border-forest" />Scheduled
          <span className="font-normal text-graphite text-[12px] num">{fmtDay(visitedAt)}</span>
        </span>
      )
    ) : <Dash />}
  </td>
);

/** Status pill over the sent → read times (and why it failed). */
export const DeliveryCell: React.FC<{
  status: string; pillCls: string; sentAt: string | null; readAt: string | null;
  failure?: string | null; freeform?: boolean;
}> = ({ status, pillCls, sentAt, readAt, failure, freeform }) => (
  <td className="px-3 py-3 whitespace-nowrap">
    <div className="flex items-center gap-1.5">
      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${pillCls}`}>{status}</span>
      {freeform && <span className="text-[11px] text-graphite">free-form</span>}
    </div>
    {failure
      ? <div className="text-[11.5px] mt-1 text-brick">{failure}</div>
      : <div className="text-[11.5px] mt-1 text-graphite num">{fmtSentRead(sentAt, readAt)}</div>}
  </td>
);

/** "1–25 of 312" and ‹ 1 2 3 … N › over a server-paged list. */
export const Pager: React.FC<{ skip: number; limit: number; total: number; onSkip: (skip: number) => void }> = ({ skip, limit, total, onSkip }) => {
  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(skip / limit) + 1;
  const shown = Array.from(new Set([1, page - 1, page, page + 1, pages].filter((p) => p >= 1 && p <= pages))).sort((a, b) => a - b);
  const cls = (on: boolean) => `min-w-[32px] h-8 px-2 rounded-lg text-[12.5px] font-semibold num transition-colors ${
    on ? 'bg-forest text-warm-white' : 'text-graphite hover:text-charcoal hover:bg-charcoal/5'} disabled:opacity-30 disabled:hover:bg-transparent`;
  return (
    <div className="flex items-center gap-1 px-3 pt-4 border-t border-charcoal/10 text-[12.5px] text-graphite">
      <span className="num">
        {total === 0 ? 'Nothing to show' : <><b className="text-charcoal font-semibold">{skip + 1}–{Math.min(skip + limit, total)}</b> of {total.toLocaleString('en-IN')}</>}
      </span>
      <span className="flex-1" />
      {pages > 1 && (
        <>
          <button className={cls(false)} disabled={page <= 1} onClick={() => onSkip(skip - limit)} aria-label="previous page">‹</button>
          {shown.map((p, i) => (
            <React.Fragment key={p}>
              {i > 0 && shown[i - 1] !== p - 1 && <span className="px-1 text-charcoal/30">…</span>}
              <button className={cls(p === page)} onClick={() => onSkip((p - 1) * limit)}>{p}</button>
            </React.Fragment>
          ))}
          <button className={cls(false)} disabled={page >= pages} onClick={() => onSkip(skip + limit)} aria-label="next page">›</button>
        </>
      )}
    </div>
  );
};
